import React, { useCallback, useState } from 'react';
import {
    Container,
    Header,
    SpaceBetween,
    Button,
    FormField,
    Input,
    Box,
    StatusIndicator,
} from '@cloudscape-design/components';

let nextId = 2;

// ── Allowlisted domains ───────────────────────────────────────────────────
// Kept in sync with backend/steering-generator/agent.py + mcp_server.py.
// The `aws.amazon.com/blogs` entry is a dead legacy value — the check is
// host-only so paths in the allowlist never match. Left in place to avoid
// churning the display string; it's a no-op given `aws.amazon.com` above.
const ALLOWED_DOMAINS = [
    'aws.amazon.com',
    'amazonaws.com',
    'aws.amazon.com/blogs',
    'repost.aws',
    'docs.aws.amazon.com',
    'builder.aws.com',
    // AWS whitepaper PDFs are served from d<N>.awsstatic.com. Listing
    // awsstatic.com covers every subdomain via the endsWith check.
    'awsstatic.com',
    // AWS Workshop Studio. `workshops.aws` alone covers every subdomain
    // (catalog.workshops.aws, catalog.<region>.prod.workshops.aws, etc.)
    // via the endsWith('.' + d) check below, but the regional catalog
    // host is listed explicitly for readability.
    'workshops.aws',
    'catalog.us-east-1.prod.workshops.aws',
];

function validateUrl(raw) {
    const url = raw.trim();
    if (!url) return { valid: false, error: '' };
    try {
        const parsed = new URL(url);
        if (!['http:', 'https:'].includes(parsed.protocol)) {
            return { valid: false, error: 'URL must use http or https' };
        }
        const host = parsed.hostname.toLowerCase();
        const allowed = ALLOWED_DOMAINS.some(
            d => host === d || host.endsWith('.' + d)
        );
        if (!allowed) {
            return {
                valid: false,
                error: `Domain not allowed. Accepted: ${ALLOWED_DOMAINS.join(', ')}`,
            };
        }
        return { valid: true, error: '' };
    } catch {
        return { valid: false, error: 'Invalid URL format' };
    }
}

// Split text into URL lines (newline or comma separated)
function splitUrls(text) {
    return text
        .split(/[\n\r,]+/)
        .map(l => l.trim())
        .filter(l => l.length > 0);
}

const MAX_URLS = 10;

const EXAMPLE_URLS = [
    'https://aws.amazon.com/blogs/networking-and-content-delivery/amazon-s3-amazon-cloudfront-a-match-made-in-the-cloud/',
    'https://aws.amazon.com/blogs/networking-and-content-delivery/introducing-flat-rate-pricing-plans-with-no-overages/',
];

export default function UrlInputPanel({
    urlEntries,
    setUrlEntries,
    isProcessing,
    connected,
    onSubmit,
    onStop,
    onClear,
    charLimit,
    setCharLimit,
}) {
    const [touched, setTouched] = useState({});

    const addRow = useCallback(() => {
        setUrlEntries(prev => prev.length >= MAX_URLS ? prev : [...prev, { id: nextId++, url: '' }]);
    }, [setUrlEntries]);

    const removeRow = useCallback((id) => {
        setUrlEntries(prev => prev.filter(e => e.id !== id));
        setTouched(prev => { const n = { ...prev }; delete n[id]; return n; });
    }, [setUrlEntries]);

    const updateUrl = useCallback((id, value) => {
        setUrlEntries(prev => prev.map(e => e.id === id ? { ...e, url: value } : e));
    }, [setUrlEntries]);

    const markTouched = useCallback((id) => {
        setTouched(prev => ({ ...prev, [id]: true }));
    }, []);

    // Paste into a row — if multiple URLs, expand into separate rows (capped at MAX_URLS total)
    const handlePaste = useCallback((id, e) => {
        const pasted = e.clipboardData?.getData('text') || '';
        const lines = splitUrls(pasted);
        if (lines.length <= 1) return; // single URL — let default paste handle it

        e.preventDefault();
        setUrlEntries(prev => {
            const otherCount = prev.filter(entry => entry.id !== id).length;
            const capped = lines.slice(0, MAX_URLS - otherCount);
            const newIds = capped.map((_, i) => i === 0 ? id : nextId++);
            const idx = prev.findIndex(entry => entry.id === id);
            const without = prev.filter(entry => entry.id !== id);
            const newRows = capped.map((url, i) => ({ id: newIds[i], url }));
            const result = [...without.slice(0, idx), ...newRows, ...without.slice(idx)];
            setTouched(t => {
                const next = { ...t };
                newIds.forEach(rid => { next[rid] = true; });
                return next;
            });
            return result;
        });
    }, [setUrlEntries]);

    // Paste from clipboard button — reads clipboard and populates rows (capped at MAX_URLS)
    const pasteFromClipboard = useCallback(async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (!text.trim()) return;
            const lines = splitUrls(text);
            if (!lines.length) return;

            setUrlEntries(prev => {
                const hasOnlyBlank = prev.length === 1 && !prev[0].url.trim();
                const base = hasOnlyBlank ? [] : prev;
                const available = MAX_URLS - base.length;
                const capped = lines.slice(0, available);
                const newRows = capped.map(url => ({ id: nextId++, url }));
                return [...base, ...newRows];
            });
        } catch {
            // Clipboard API unavailable — user can paste directly into the input
        }
    }, [setUrlEntries]);

    const loadExamples = useCallback(() => {
        setUrlEntries(EXAMPLE_URLS.map((url, i) => ({ id: i + 1, url })));
        nextId = EXAMPLE_URLS.length + 1;
        setTouched({});
    }, [setUrlEntries]);

    const validEntries = urlEntries.filter(e => validateUrl(e.url).valid);
    const validCount   = validEntries.length;
    const hasErrors    = urlEntries.some(e => e.url.trim() && !validateUrl(e.url).valid);
    const atLimit      = urlEntries.length >= MAX_URLS;

    return (
        <Container
            header={
                <Header
                    variant="h2"
                    description={
                        <>
                            Enter AWS blog post URLs — one per row (max {MAX_URLS}). Title, services, authors, and
                            date are inferred automatically from the page content.
                            <br />
                            <Box fontSize="body-s" color="text-body-secondary" display="inline">
                                Allowed domains: {ALLOWED_DOMAINS.join(' · ')}
                            </Box>
                        </>
                    }
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" iconName="upload" onClick={pasteFromClipboard} disabled={isProcessing}>
                                Paste from clipboard
                            </Button>
                            <Button variant="link" onClick={loadExamples} disabled={isProcessing}>
                                Load examples
                            </Button>
                            <Button variant="normal" onClick={onClear} disabled={isProcessing}>
                                Clear all
                            </Button>
                        </SpaceBetween>
                    }
                >
                    Blog URLs
                </Header>
            }
        >
            <SpaceBetween direction="vertical" size="s">
                {urlEntries.map((entry) => {
                    const { valid, error } = validateUrl(entry.url);
                    const showError = touched[entry.id] && entry.url.trim() && !valid;

                    return (
                        <div
                            key={entry.id}
                            style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', width: '100%' }}
                            onPaste={(e) => handlePaste(entry.id, e)}
                        >
                            {/* URL input — full width, scrollable */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <FormField errorText={showError ? error : ''}>
                                    <div className={`sg-url-input-wrap${showError ? ' sg-url-input-error' : valid && entry.url.trim() ? ' sg-url-input-valid' : ''}`}>
                                        <input
                                            className="sg-url-input"
                                            type="url"
                                            value={entry.url}
                                            onChange={(e) => updateUrl(entry.id, e.target.value)}
                                            onBlur={() => markTouched(entry.id)}
                                            placeholder="https://aws.amazon.com/blogs/… (paste multiple URLs to add rows)"
                                            disabled={isProcessing}
                                            spellCheck={false}
                                            autoComplete="off"
                                        />
                                    </div>
                                </FormField>
                            </div>

                            {/* Validation indicator */}
                            <div style={{ paddingTop: '6px', width: '24px', flexShrink: 0 }}>
                                {entry.url.trim() && (
                                    valid
                                        ? <StatusIndicator type="success" />
                                        : touched[entry.id]
                                            ? <StatusIndicator type="error" />
                                            : null
                                )}
                            </div>

                            {/* Remove button — only when >1 row */}
                            <div style={{ paddingTop: '2px', width: '32px', flexShrink: 0 }}>
                                {urlEntries.length > 1 && (
                                    <Button
                                        variant="icon"
                                        iconName="remove"
                                        ariaLabel="Remove URL"
                                        onClick={() => removeRow(entry.id)}
                                        disabled={isProcessing}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}

                <SpaceBetween direction="horizontal" size="xs" alignItems="end">
                    <Button
                        variant="normal"
                        iconName="add-plus"
                        onClick={addRow}
                        disabled={isProcessing || atLimit}
                    >
                        Add URL{atLimit ? ` (max ${MAX_URLS})` : ''}
                    </Button>

                    <FormField
                        label="Max characters"
                        description="Cap the generated doc (default 5000)"
                    >
                        <div style={{ width: '110px' }}>
                            <Input
                                type="number"
                                inputMode="numeric"
                                value={String(charLimit ?? '')}
                                onChange={({ detail }) => {
                                    // Strip non-digits, clamp to [500, 50000].
                                    // Empty string is allowed transiently while editing.
                                    const raw = (detail.value ?? '').replace(/[^0-9]/g, '');
                                    if (raw === '') { setCharLimit(''); return; }
                                    const n = Math.min(50000, Math.max(500, parseInt(raw, 10)));
                                    setCharLimit(n);
                                }}
                                onBlur={() => {
                                    // Snap back to default if left blank or below the floor
                                    if (charLimit === '' || charLimit < 500) setCharLimit(5000);
                                }}
                                disabled={isProcessing}
                                placeholder="5000"
                            />
                        </div>
                    </FormField>

                    <Box padding={{ left: 'xl' }}>
                        {isProcessing ? (
                            <Button variant="primary" iconName="status-stopped" onClick={onStop}>
                                Stop
                            </Button>
                        ) : (
                            <Button
                                variant="primary"
                                iconName="gen-ai"
                                onClick={onSubmit}
                                disabled={validCount === 0 || hasErrors || !connected}
                            >
                                Generate {validCount > 0
                                    ? `${validCount} steering doc${validCount > 1 ? 's' : ''}`
                                    : 'steering docs'}
                            </Button>
                        )}
                    </Box>
                </SpaceBetween>
            </SpaceBetween>
        </Container>
    );
}
